// src/utils/security/privateIntegrationGate.ts

export type PrivateIntegrationGateState =
  | "pending"
  | "allowed"
  | "restricted"
  | "logged_out";

type PrivateIntegrationGateRecord = {
  state: PrivateIntegrationGateState;
  userId: number | null;
  bootId: string;
  updatedAt: number;
};

const STORAGE_KEY = "townlit_private_integrations_gate";

const BOOT_ID =
  typeof window !== "undefined"
    ? `${Date.now()}-${Math.random().toString(36).slice(2)}`
    : "server";

const isValidState = (
  value: unknown
): value is PrivateIntegrationGateState => {
  return (
    value === "pending" ||
    value === "allowed" ||
    value === "restricted" ||
    value === "logged_out"
  );
};

const readCurrentUserId = (): number | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const id = Number(parsed?.id);

    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

const parseRecord = (
  raw: string | null
): PrivateIntegrationGateRecord | null => {
  if (!raw) return null;

  // Backward compatibility with old string-based value.
  if (isValidState(raw)) {
    return {
      state: raw,
      userId: null,
      bootId: "legacy",
      updatedAt: 0,
    };
  }

  try {
    const parsed = JSON.parse(raw);

    if (!isValidState(parsed?.state)) {
      return null;
    }

    return {
      state: parsed.state,
      userId:
        typeof parsed.userId === "number" && Number.isFinite(parsed.userId)
          ? parsed.userId
          : null,
      bootId:
        typeof parsed.bootId === "string" && parsed.bootId.trim()
          ? parsed.bootId
          : "unknown",
      updatedAt:
        typeof parsed.updatedAt === "number" && Number.isFinite(parsed.updatedAt)
          ? parsed.updatedAt
          : 0,
    };
  } catch {
    return null;
  }
};

const writeRecord = (
  state: PrivateIntegrationGateState,
  userId: number | null
) => {
  if (typeof window === "undefined") return;

  const record: PrivateIntegrationGateRecord = {
    state,
    userId,
    bootId: BOOT_ID,
    updatedAt: Date.now(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));

    window.dispatchEvent(
      new CustomEvent("townlit:private-integrations-gate", {
        detail: record,
      })
    );
  } catch {
    // Keep app resilient.
  }
};

// Important: every fresh JS boot starts as pending.
// This prevents stale "allowed" from a previous page/session from firing
// private requests before profile restriction is resolved again.
const initializeGateForCurrentBoot = () => {
  if (typeof window === "undefined") return;

  const existing = parseRecord(localStorage.getItem(STORAGE_KEY));
  const currentUserId = readCurrentUserId();

  if (!existing) {
    writeRecord("pending", currentUserId);
    return;
  }

  if (existing.state === "logged_out") {
    return;
  }

  if (existing.bootId !== BOOT_ID) {
    writeRecord("pending", currentUserId);
  }
};

initializeGateForCurrentBoot();

export const setPrivateIntegrationGateState = (
  state: PrivateIntegrationGateState,
  userId?: number | null
) => {
  const resolvedUserId =
    typeof userId === "number" && Number.isFinite(userId)
      ? userId
      : readCurrentUserId();

  writeRecord(state, resolvedUserId);
};

export const getPrivateIntegrationGateState =
  (): PrivateIntegrationGateState => {
    if (typeof window === "undefined") return "pending";

    const record = parseRecord(localStorage.getItem(STORAGE_KEY));

    if (!record) {
      return "pending";
    }

    const currentUserId = readCurrentUserId();

    // Never trust allowed/restricted from an old JS boot.
    if (record.bootId !== BOOT_ID) {
      return "pending";
    }

    // If a user is logged in, the gate must belong to the same user.
    if (
      currentUserId &&
      record.userId &&
      record.userId !== currentUserId
    ) {
      return "pending";
    }

    return record.state;
  };

export const canRunPrivateIntegrationsNow = (): boolean => {
  return getPrivateIntegrationGateState() === "allowed";
};

export const shouldBlockPrivateIntegrationsNow = (): boolean => {
  const state = getPrivateIntegrationGateState();

  return (
    state === "pending" ||
    state === "restricted" ||
    state === "logged_out"
  );
};